/**
 * Layout and mode switching. Owns no engine types — it is handed already-built
 * panels and a set of callbacks by the composition root.
 *
 * Two layouts, one DOM: on wide screens the panel column sits on the left; on
 * narrow screens it becomes a bottom sheet toggled by a floating button. The
 * switch lives in CSS (`.sidebar`), not in JS, so there is no resize listener
 * and no breakpoint duplicated between the two.
 */
import { el, mount, setVisible } from "./components/dom";

export type ViewMode = "3d" | "plan" | "pdf";

export interface ShellCallbacks {
  onModeChange(mode: ViewMode): void;
  onPlanSelect(id: string): void;
  onResetVisibility(): void;
}

export interface AppShell {
  readonly viewport: HTMLElement;
  readonly sidebar: HTMLElement;
  readonly overlay: HTMLElement;
  setPlans(plans: readonly { id: string; label: string }[]): void;
  setMode(mode: ViewMode): void;
  setReady(ready: boolean): void;
}

const MODES: readonly { id: ViewMode; label: string }[] = [
  { id: "3d", label: "3D" },
  { id: "plan", label: "Plan" },
  { id: "pdf", label: "PDF" },
];

export function createAppShell(host: HTMLElement, callbacks: ShellCallbacks): AppShell {
  const layout = el("div", "relative h-full w-full");
  const viewport = el("div", "absolute inset-0");
  const overlay = el("div", "absolute inset-0 pointer-events-none");

  const sidebar = el("aside", "sidebar");
  sidebar.dataset["open"] = "false";
  setVisible(sidebar, false);

  const modeBar = el("div", "glass enter mode-bar");
  setVisible(modeBar, false);

  const modeButtons = new Map<ViewMode, HTMLButtonElement>();
  for (const mode of MODES) {
    const node = el("button", "btn", mode.label);
    node.type = "button";
    node.addEventListener("click", () => {
      callbacks.onModeChange(mode.id);
    });
    modeButtons.set(mode.id, node);
    modeBar.appendChild(node);
  }

  // Mobile-only handle for the bottom sheet; CSS hides it on wide screens.
  const sheetToggle = el("button", "glass panel-toggle", "Panels");
  sheetToggle.type = "button";
  sheetToggle.setAttribute("aria-controls", "panel-sheet");
  sidebar.id = "panel-sheet";
  setVisible(sheetToggle, false);
  sheetToggle.addEventListener("click", () => {
    const open = sidebar.dataset["open"] !== "true";
    sidebar.dataset["open"] = String(open);
    sheetToggle.setAttribute("aria-expanded", String(open));
    sheetToggle.textContent = open ? "Close" : "Panels";
  });

  const planPicker = el("section", "glass enter flex min-h-0 flex-col gap-1 p-3");
  const planBody = el("div", "panel-body flex flex-col gap-0.5");
  const planHeader = headerWithAction("Storey plans", "Reset", () => {
    callbacks.onResetVisibility();
  });
  mount(planPicker, planHeader, planBody);
  setVisible(planPicker, false);

  mount(layout, viewport, overlay, modeBar, sheetToggle, sidebar);
  host.appendChild(layout);

  return {
    viewport,
    sidebar,
    overlay,

    setPlans(plans) {
      planBody.replaceChildren();
      setVisible(planPicker, plans.length > 0);
      for (const plan of plans) {
        const row = el("button", "row");
        row.type = "button";
        row.textContent = plan.label;
        row.addEventListener("click", () => {
          for (const child of planBody.children) child.removeAttribute("data-active");
          row.dataset["active"] = "true";
          callbacks.onPlanSelect(plan.id);
        });
        planBody.appendChild(row);
      }
      if (!sidebar.contains(planPicker)) sidebar.appendChild(planPicker);
    },

    setMode(mode) {
      for (const [id, node] of modeButtons) {
        node.dataset["active"] = String(id === mode);
      }
      setVisible(planPicker, mode === "plan" && planBody.children.length > 0);
    },

    setReady(ready) {
      setVisible(sidebar, ready);
      setVisible(modeBar, ready);
      setVisible(sheetToggle, ready);
    },
  };
}

function headerWithAction(title: string, actionLabel: string, onAction: () => void): HTMLElement {
  const header = el("div", "flex items-center justify-between px-1 pb-1");
  const action = el("button", "link-action", actionLabel);
  action.type = "button";
  action.addEventListener("click", onAction);
  mount(header, el("span", "panel-title", title), action);
  return header;
}
